import { FC } from 'react';
import { t } from 'i18next';
import { Box, Button, IconButton } from '@mui/material';
import { Add, Edit } from '@mui/icons-material';
import { Form, FormProvider } from 'react-hook-form';

import {
  BuildingSearchFormField,
  DatepickerFormField,
  NumberInputFormField,
  PracticeSearchFormField,
  ProviderSearchFormField,
  TextAreaFormField,
  TextFormField,
} from 'components/DynamicFormFields';
import { CustomModal, SaveChangesModal } from '@components';
import { useAddActivityLog } from '../hooks';
import { MAXIMUM_TEXT_AREA_LENGTH } from '@common/types/constants';

interface AddActivityLogProps {
  variant: 'edit' | 'create';
  disabled?: boolean;
  activity_id?: string;
}

export const AddActivityLog: FC<AddActivityLogProps> = ({
  variant,
  activity_id = '',
  disabled = false,
}) => {
  const {
    isOpen,
    handleClose,
    handleOpen,
    onSubmit,
    isSubmitting,
    formMethods,
    isSaveChangesModalOpen,
    handleCloseSaveChangesModal,
    handleDiscardChanges,
    handleSaveChanges,
    isValid,
    isEditSubmitting,
  } = useAddActivityLog(activity_id, variant);

  const { control } = formMethods;

  return (
    <>
      {variant === 'create' ? (
        <Button
          onClick={handleOpen}
          startIcon={<Add />}
          sx={{
            minHeight: '30px',
            fontWeight: 'regular',
            fontSize: '1rem',
            color: 'white',
          }}
          color='secondary'
          disabled={disabled}
          variant='contained'
        >
          ADD ACTIVITY
        </Button>
      ) : (
        <IconButton
          sx={{
            '& .MuiIconButton': {
              padding: '0.5rem',
            },
          }}
          disabled={disabled}
          onClick={e => {
            e.preventDefault();
            e.stopPropagation();

            handleOpen();
          }}
        >
          <Edit color={disabled ? 'disabled' : 'primary'} />
        </IconButton>
      )}
      <Box onClick={e => e.stopPropagation()}>
        <CustomModal
          isOpen={isOpen && !disabled}
          onClose={handleClose}
          isLoading={isSubmitting}
          title={variant === 'create' ? 'Add activity' : 'Edit activity'}
        >
          <FormProvider {...formMethods}>
            <Form
              control={control}
              onSubmit={e => onSubmit(e.event)}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: '16px',
              }}
            >
              <TextFormField
                name='title'
                label='Activity name'
                optional={false}
              />

              <DatepickerFormField
                name='timestamp'
                label='Activity date'
                disableFuture
                optional={false}
              />

              <TextAreaFormField
                optional
                maxRows={4}
                disabled={disabled}
                name='description'
                label='Description (optional)'
                charLimit={MAXIMUM_TEXT_AREA_LENGTH}
              />

              <NumberInputFormField
                name='recorded_minutes'
                label='Minutes'
                optional={false}
              />

              <ProviderSearchFormField
                name='provider_id'
                placeholder='Provider (optional)'
                optional
              />

              <PracticeSearchFormField
                name='practice_id'
                placeholder='Practice (optional)'
                optional
              />

              <BuildingSearchFormField
                name='building_id'
                placeholder='Building (optional)'
                optional
              />

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: '8px',
                  paddingY: 3,
                  position: 'sticky',
                  bottom: 0,
                  left: 0,
                  zIndex: 3,
                  background: '#fff',
                }}
              >
                <Button
                  variant='text'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                  }}
                  disabled={isSubmitting}
                  onClick={handleClose}
                >
                  {t('general.cancel')}
                </Button>
                <Button
                  type='submit'
                  variant='contained'
                  color='secondary'
                  sx={{
                    fontSize: '0,875rem',
                    fontWeight: 'medium',
                    lineHeight: '24px',
                    color: 'white',
                  }}
                  disabled={isSubmitting || isEditSubmitting || !isValid}
                  loading={isEditSubmitting}
                  loadingPosition='start'
                >
                  {t(`general.${isEditSubmitting ? 'saving' : 'save'}`)}
                </Button>
              </Box>
              <SaveChangesModal
                isOpen={isSaveChangesModalOpen}
                onClose={handleCloseSaveChangesModal}
                isValid={isValid}
                handleSaveChanges={handleSaveChanges}
                discardChanges={handleDiscardChanges}
                isLoading={isEditSubmitting}
              />
            </Form>
          </FormProvider>
        </CustomModal>
      </Box>
    </>
  );
};
